/* global google */
import React, { useEffect, useState, useRef, useCallback } from "react";

const GOOGLE_PAY_SHIPPING_OPTIONS = [
  {
    id: "standard",
    label: "Standard Shipping",
    description: "Arrives in 5 to 7 days",
    price: "0.00",
  },
  {
    id: "express",
    label: "Express Shipping",
    description: "Arrives in 1 to 2 days",
    price: "5.00",
  },
];

// Update price and line items to reflect selected shipping option
function updateTransactionForShipping(settings, selectedOptionId) {
  const option =
    GOOGLE_PAY_SHIPPING_OPTIONS.find((o) => o.id === selectedOptionId) ||
    GOOGLE_PAY_SHIPPING_OPTIONS[0];
  const baseAmount = Number(settings.amount);
  const shippingAmount = Number(option.price);
  const total = (baseAmount + shippingAmount).toFixed(2);
  return {
    newTransactionInfo: {
      displayItems: [
        { label: "Subtotal", type: "LINE_ITEM", price: baseAmount.toFixed(2) },
        {
          label: option.label,
          type: "SHIPPING_OPTION",
          price: option.price,
        },
      ],
      totalPriceStatus: "FINAL",
      totalPrice: total,
      totalPriceLabel: "Total",
      currencyCode: settings.currency,
      countryCode: settings.country,
    },
  };
}

function handlePaymentDataChanged(newData, settings) {
  const optionId =
    newData.shippingOptionData?.id ?? GOOGLE_PAY_SHIPPING_OPTIONS[0].id; // Default to standard shipping
  return updateTransactionForShipping(settings, optionId);
}

function buildTransactionInfo(settings) {
  const amount = Number(settings.amount).toFixed(2);

  // Used by all MPAN payment types
  const mpanCommon = {
    currencyCode: settings.currency,
    countryCode: settings.country,
    tokenUpdateUrl: settings.tokenUpdateUrl,
    managementUrl: settings.managementUrl,
    immediateTotalPrice: amount,
  };

  switch (settings.paymentType) {
    case "recurring":
      return {
        key: "recurringTransactionInfo",
        value: {
          ...mpanCommon,
          recurrenceItems: [
            {
              label: "Recurring charge",
              price: amount,
              priceStatus: "FINAL",
              recurrencePeriod: "MONTH",
              recurrencePeriodCount: 1,
            },
          ],
        },
      };

    case "deferred":
      return {
        key: "deferredTransactionInfo",
        value: {
          ...mpanCommon,
          label: "Deferred charge",
          // 30 days from today
          billingDateTime: new Date(
            Date.now() + 30 * 24 * 60 * 60 * 1000,
          ).toISOString(),
          priceStatus: "FINAL",
          price: amount,
        },
      };

    case "autoReload":
      return {
        key: "automaticReloadTransactionInfo",
        value: {
          ...mpanCommon,
          label: "Automatic top-up",
          minimumBalanceAmount: "10.00",
          reloadAmount: amount,
        },
      };

    // Standard DPAN transaction
    case "transaction":
    default:
      return {
        key: "transactionInfo",
        value: {
          totalPriceStatus: "FINAL",
          totalPrice: settings.requireShippingMethod
            ? (
                Number(settings.amount) +
                Number(GOOGLE_PAY_SHIPPING_OPTIONS[0].price)
              ).toFixed(2)
            : settings.amount.toString(),
          currencyCode: settings.currency,
          countryCode: settings.country,
          ...(settings.requireShippingMethod && {
            displayItems: [
              {
                label: "Subtotal",
                type: "LINE_ITEM",
                price: Number(settings.amount).toFixed(2),
              },
              {
                label: GOOGLE_PAY_SHIPPING_OPTIONS[0].label,
                type: "SHIPPING_OPTION",
                price: GOOGLE_PAY_SHIPPING_OPTIONS[0].price,
              },
            ],
            totalPriceLabel: "Total",
          }),
        },
      };
  }
}

const GooglePayButton = ({ handleTokenOutput, googlePaySettings }) => {
  const [canUseGooglePay, setCanUseGooglePay] = useState(false);
  const [loading, setLoading] = useState(true);
  const googlePayButtonRef = useRef(null);
  const buttonCreatedRef = useRef(false);

  const buildPaymentDataRequest = (googlePaySettings) => {
    const transactionInfoField = buildTransactionInfo(googlePaySettings);
    // Dynamic pricing not supported for MPAN payment types
    const isStandardTransaction =
      transactionInfoField.key === "transactionInfo";
    const useShipping =
      isStandardTransaction && googlePaySettings.requireShippingMethod;
    return {
      apiVersion: 2,
      apiVersionMinor: 0,
      allowedPaymentMethods: [
        {
          type: "CARD",
          parameters: {
            allowedAuthMethods: [
              ...(googlePaySettings.allowCryptogram3ds
                ? ["CRYPTOGRAM_3DS"]
                : []),
              ...(googlePaySettings.allowPanOnly ? ["PAN_ONLY"] : []),
            ],
            allowedCardNetworks: [
              ...(googlePaySettings.allowVisa ? ["VISA"] : []),
              ...(googlePaySettings.allowMastercard ? ["MASTERCARD"] : []),
              ...(googlePaySettings.countryCode === "BR" &&
              googlePaySettings.allowMaestro
                ? ["MAESTRO"]
                : []),
              // ...(googlePaySettings.countryCode === "BR" && googlePaySettings.allowElo ? ["ELO", "ELO_DEBIT"] : []),
              ...(googlePaySettings.allowAmex ? ["AMEX"] : []),
              ...(googlePaySettings.allowDiscover ? ["DISCOVER"] : []),
              ...(googlePaySettings.allowJcb ? ["JCB"] : []),
            ],
            ...(googlePaySettings.requireBillingContact && {
              billingAddressRequired: true,
              billingAddressParameters: {
                format: "FULL",
              },
            }),
            ...(!googlePaySettings.allowPrepaid && {
              allowPrepaidCards: false,
            }),
            ...(!googlePaySettings.allowCredit && {
              allowCreditCards: false,
            }),
          },
          tokenizationSpecification: {
            type: "PAYMENT_GATEWAY",
            parameters: {
              gateway: "checkoutltd",
              gatewayMerchantId: googlePaySettings.gatewayMerchantId,
            },
          },
        },
      ],
      merchantInfo: {
        merchantName: "Demo Merchant",
        merchantId: googlePaySettings.merchantId,
      },
      [transactionInfoField.key]: transactionInfoField.value,
      ...(isStandardTransaction &&
        googlePaySettings.requireShippingContact && {
          shippingAddressRequired: true,
          shippingAddressParameters: {
            phoneNumberRequired: true,
          },
        }),
      ...(useShipping && {
        callbackIntents: [
          ...(googlePaySettings.requireShippingContact
            ? ["SHIPPING_ADDRESS"]
            : []),
          "SHIPPING_OPTION",
          "PAYMENT_AUTHORIZATION",
        ],
        shippingOptionRequired: true,
        shippingOptionParameters: {
          defaultSelectedOptionId: GOOGLE_PAY_SHIPPING_OPTIONS[0].id,
          shippingOptions: GOOGLE_PAY_SHIPPING_OPTIONS.map(
            ({ id, label, description }) => ({ id, label, description }),
          ),
        },
      }),
    };
  };

  const getGooglePayEnvironment = (gatewayMerchantId) => {
    if (!gatewayMerchantId) return "PRODUCTION";
    return gatewayMerchantId.startsWith("pk_sbox_") ||
      gatewayMerchantId.startsWith("pk_qa_")
      ? "TEST"
      : "PRODUCTION";
  };

  // Memoized function to initialize Google Pay
  const loadGooglePay = useCallback(async () => {
    const environment = getGooglePayEnvironment(
      googlePaySettings.gatewayMerchantId,
    );
    const paymentsClient = new google.payments.api.PaymentsClient({
      environment,
    });

    const isReadyToPayRequest = {
      apiVersion: 2,
      apiVersionMinor: 0,
      allowedPaymentMethods: [
        {
          type: "CARD",
          parameters: {
            allowedAuthMethods: [
              ...(googlePaySettings.allowCryptogram3ds
                ? ["CRYPTOGRAM_3DS"]
                : []),
              ...(googlePaySettings.allowPanOnly ? ["PAN_ONLY"] : []),
            ],
            allowedCardNetworks: [
              ...(googlePaySettings.allowVisa ? ["VISA"] : []),
              ...(googlePaySettings.allowMastercard ? ["MASTERCARD"] : []),
              ...(googlePaySettings.allowAmex ? ["AMEX"] : []),
              ...(googlePaySettings.allowDiscover ? ["DISCOVER"] : []),
              ...(googlePaySettings.allowJcb ? ["JCB"] : []),
            ],
          },
        },
      ],
    };

    try {
      const response = await paymentsClient.isReadyToPay(isReadyToPayRequest);
      setCanUseGooglePay(response.result);
    } catch (error) {
      console.error("Error loading Google Pay:", error);
      setCanUseGooglePay(false);
    } finally {
      setLoading(false);
    }
  }, [googlePaySettings]);

  const createGooglePayButton = useCallback(async () => {
    const environment = getGooglePayEnvironment(
      googlePaySettings.gatewayMerchantId,
    );
    const paymentClientOptions = { environment };
    if (googlePaySettings.requireShippingMethod) {
      paymentClientOptions.paymentDataCallbacks = {
        onPaymentDataChanged: (intermediatePaymentData) =>
          Promise.resolve(
            handlePaymentDataChanged(
              intermediatePaymentData,
              googlePaySettings,
            ),
          ),
        onPaymentAuthorized: () =>
          Promise.resolve({ transactionState: "SUCCESS" }),
      };
    }
    const paymentsClient = new google.payments.api.PaymentsClient(
      paymentClientOptions,
    );

    const button = paymentsClient.createButton({
      onClick: async () => {
        const paymentDataRequest = buildPaymentDataRequest(googlePaySettings);
        try {
          const paymentData =
            await paymentsClient.loadPaymentData(paymentDataRequest);
          handleTokenOutput(paymentData, "Google Pay");
        } catch (error) {
          console.error("Error processing Google Pay request:", error);
        }
      },
      buttonColor: googlePaySettings.buttonStyle,
      buttonType: googlePaySettings.buttonType,
      buttonLocale: googlePaySettings.buttonLanguage,
      buttonSizeMode: "fill",
    });

    // Clear previous button if exists
    if (googlePayButtonRef.current) {
      googlePayButtonRef.current.innerHTML = "";
      googlePayButtonRef.current.appendChild(button);
      buttonCreatedRef.current = true; // Prevent further button creations
    }
  }, [googlePaySettings, handleTokenOutput]);

  // Check Google Pay availability and initialize on load
  useEffect(() => {
    loadGooglePay();
  }, [loadGooglePay]);

  useEffect(() => {
    if (canUseGooglePay && !buttonCreatedRef.current) {
      createGooglePayButton();
    }
  }, [canUseGooglePay, createGooglePayButton]);

  // Watch for changes in googlePaySettings to recreate the button
  useEffect(() => {
    if (canUseGooglePay && buttonCreatedRef.current) {
      createGooglePayButton(); // Recreate button on setting change
      buttonCreatedRef.current = false; // Reset to allow re-creation
    }
  }, [googlePaySettings, canUseGooglePay, createGooglePayButton]);

  // Ensure the button is re-rendered if it doesn't appear initially
  useEffect(() => {
    if (
      canUseGooglePay &&
      !document.querySelector("#gpay-button-online-api-id")
    ) {
      setCanUseGooglePay(false);
      setTimeout(() => setCanUseGooglePay(true), 0);
    }
  }, [canUseGooglePay]);

  return (
    <>
      {loading ? (
        <div
          style={{
            marginBottom: "20px",
            width: "240px",
            height: "40px",
          }}
        ></div>
      ) : canUseGooglePay ? (
        <div
          ref={googlePayButtonRef}
          style={{
            marginBottom: "20px",
            width: "240px",
            height: "40px",
          }}
        >
          {/* Google Pay button will be inserted here */}
        </div>
      ) : (
        <div></div>
      )}
    </>
  );
};

export default GooglePayButton;
